import React from 'react';
import Text from '../components/elements/TextComponent';
import Button from '../components/elements/Button';
import userAtDesk from '../assets/userAtDesk.svg';
import ImageComponent from '../components/elements/ImageComponent';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';


const EMBED_SRC =
  "https://api.comechatwithme.ai/chat/embed/v2?botId=69e298ae05b0d5fad95f464d&name=Jester&mode=inline&variant=default&theme=%23B04BFF&radius=16px&welcome=Hi%20I%27m%20Jester%21%20Ask%20me%20for%20a%20joke%2C%20pun%20or%20for%20something%20funny&launcher=true&w=320&h=540&font=Inter%2C%20system-ui%2C%20sans-serif&fontUrl=https%3A%2F%2Ffonts.googleapis.com%2Fcss2%3Ffamily%3DInter%3Awght%40400%3B600%26display%3Dswap&allowSession=false&suggest=Give%20me%20a%20long%20joke%20with%20a%20punch%20line&suggest=Write%20a%20sarcastic%20haiku%20about%20Mondays";

  
function ChatEmbed({
  style,
  inline = true, // default: inline in-flow
}: { style?: React.CSSProperties; inline?: boolean }) {
  const base: React.CSSProperties = {
    border: 0,
    display: 'block', // removes inline baseline gap
  };

  const inlineStyle: React.CSSProperties = {
    width: '100%',
    height: 540,
  };

  const fixedStyle: React.CSSProperties = {
    position: 'fixed',
    right: 16,
    bottom: 16,
    width: '100%',
    height: 540,
    zIndex: 2147483647,
  };

  return (
    <iframe
      title="Giggles Chat"
      src={EMBED_SRC}
      style={{ ...base, ...(inline ? inlineStyle : fixedStyle), ...style }}
      scrolling="no"
      // sandbox="allow-scripts allow-same-origin allow-forms"
      // referrerPolicy="no-referrer"
    />
  );
}


const ArrowCue: React.FC<{
  onClick?: () => void;
  direction?: 'down' | 'up';
  fixed?: boolean;          // fixed to viewport (default) or absolute
}> = ({ onClick, direction = 'down', fixed = true }) => {
  const reduce = useReducedMotion();
  const bounce = direction === 'up' ? [-6, 0, -6] : [0, 6, 0];

  return (
    <motion.button
      type="button"
      aria-label={direction === 'up' ? 'Scroll up' : 'Scroll down'}
      onClick={onClick}
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut', delay: 0.5 }}
      style={{
        position: fixed ? 'fixed' : 'absolute',
        left: '50%',
        bottom: 16,
        transform: 'translateX(-50%)',
        background: 'transparent',
        border: 0,
        cursor: 'pointer',
        padding: 8,
        borderRadius: 999,
        outlineOffset: 4,
        zIndex: 10,
      }}
    >
      <motion.div
        animate={reduce ? {} : { y: bounce }}
        transition={reduce ? {} : { duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
        style={{ display: 'grid', placeItems: 'center' }}
      >
        <svg
          width="108"
          height="108"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          style={{ transform: direction === 'up' ? 'rotate(180deg)' : 'none' }}
        >
          <path d="M6 8l6 6 6-6" stroke="var(--primary, #8b2cd7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M6 12l6 6 6-6" stroke="var(--primary, #8b2cd7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.6"/>
        </svg>
      </motion.div>
    </motion.button>
  );
};


const BotCard: React.FC<{
  title: string;
  desc: string;
  badge?: string;
  onClick?: () => void;
}> = ({ title, desc, badge, onClick }) => {
  return (
    <motion.div
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
      style={{
        border: '1px solid var(--bd, #e5e7eb)',
        borderRadius: 16,
        padding: 16,
        background: '#fff',
        boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        {/* Minimal icon bubble that matches your theme */}
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 999,
            background: 'var(--primary, #8b2cd7)',
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          {/* first letter as a quick visual */}
          {title.charAt(0).toUpperCase()}
        </div>
        <Text text={title} fontSize="16px" fontWeight="700" lineHeight="20px" color="#111827" />
        {badge && (
          <span
            style={{
              marginLeft: 'auto',
              fontFamily: 'Montserrat',
              fontSize: 12,
              color: 'var(--primary, #8b2cd7)',
              background: 'rgba(139,44,215,0.08)',
              border: '1px solid rgba(139,44,215,0.25)',
              padding: '4px 8px',
              borderRadius: 999,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {badge}
          </span>
        )}
      </div>
      <Text
        text={desc}
        fontSize="14px"
        fontWeight="400"
        lineHeight="22px"
        color="#374151"
        textAlign="left"
      />
    </motion.div>
  );
};

const Home: React.FC = () => {
  const navigate = useNavigate();

   // Scroll container ref (your outer wrapper is the scroller)
  const scrollerRef = React.useRef<HTMLDivElement>(null);

  const reduce = useReducedMotion();
  // Scroll exactly one viewport height (100vh) per click
  const scrollOneViewportDown = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const h = el.clientHeight;                // equals 100vh due to styles
    const max = el.scrollHeight - h;
    const next = Math.min(el.scrollTop + h, max);
    el.scrollTo({ top: next, behavior: reduce ? 'auto' : 'smooth' });
  };

  const scrollOneViewportUp = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const h = el.clientHeight;
    const next = Math.max(el.scrollTop - h, 0);
    el.scrollTo({ top: next, behavior: reduce ? 'auto' : 'smooth' });
  };
  const [isAtBottom, setIsAtBottom] = React.useState(false);
const [isScrollable, setIsScrollable] = React.useState(false);

React.useEffect(() => {
  const el = scrollerRef.current;
  if (!el) return;

  const updateFlags = () => {
    const h = el.clientHeight;
    const sh = el.scrollHeight;
    const st = el.scrollTop;
    setIsScrollable(sh > h + 1);
    setIsAtBottom(st >= sh - h - 1);
  };

  updateFlags();
  el.addEventListener('scroll', updateFlags, { passive: true });
  const ro = new ResizeObserver(updateFlags);
  ro.observe(el);
  window.addEventListener('resize', updateFlags);

  return () => {
    el.removeEventListener('scroll', updateFlags);
    ro.disconnect();
    window.removeEventListener('resize', updateFlags);
  };
}, []);



  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.98 }}
      transition={{ duration: 0.35, ease: 'easeInOut' }}
    >
      <div ref={scrollerRef} style={{ overflowY: 'auto', height: '100vh', scrollSnapType: 'y mandatory' }}>
        <div className="split-screen">
          {/* Left side */}
          <div className="left-side-border-bottom-right-16 color-purple-background">
            <div className="margin-top-20p" style={{ width: '50vw', height: '100vh' }}>
              <ImageComponent src={userAtDesk} center />
              <Text
                text="ComeChatWithMe.ai"
                fontSize="32px"
                fontWeight="700"
                lineHeight="38px"
                color="#ffffff"
                textAlign="center"
                navigateToHome
              />
              <div className="margin-top-30">
                <Text
                  text="Create and share personalized assistants effortlessly."
                  fontSize="18px"
                  fontWeight="400"
                  lineHeight="18px"
                  color="#ffffff"
                  textAlign="center"
                />
              </div>
            </div>
          </div>

          {/* Right side */}
          <div
            className="right-side"
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
          >
            <div style={{ maxWidth: 520, padding: '0 40px' }}>
              <Text
                text="Build Custom Intelligent Assistants — Easy to Embed on Your Site. Now free to use"
                fontSize="32px"
                fontWeight="700"
                lineHeight="38px"
                color="#030303"
                textAlign="left"
              />
              <div className="margin-bottom-20"></div>

              <Text
                text="Create branded AI assistants in minutes—no ML required."
                fontSize="16px"
                fontWeight="400"
                lineHeight="28px"
                color="#030303"
                textAlign="left"
              />

              <Text
                text="Connect your content (PDFs, docs, FAQs), set instructions, tone, guardrails, and tailor capabilities to your use case."
                fontSize="16px"
                fontWeight="400"
                lineHeight="28px"
                color="#030303"
                textAlign="left"
              />

              <Text
                text="Embed with a single iframe, script tag, or React component. Choose inline chat or a floating launcher, and match your brand with theme color and fonts."
                fontSize="16px"
                fontWeight="400"
                lineHeight="28px"
                color="#030303"
                textAlign="left"
              />

              <Text
                text="Track engagement and view conversations via our comprehensive analytics."
                fontSize="16px"
                fontWeight="400"
                lineHeight="28px"
                color="#030303"
                textAlign="left"
              />


              {/* CTA Button */}
              <div style={{ marginTop: 24 }}>
                <Button label="Get Started" width="100%" height="42px" onClick={() => navigate('/login')} />
              </div>

              {/* Links */}
              <div className="margin-top-20" style={{ fontSize: 14 }}>
                <a
                  href="/termsofservice"
                  style={{
                    color: 'var(--primary)',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    textDecoration: 'underline',
                    marginRight: 12,
                  }}
                >
                  Terms of Service
                </a>
                <a
                  href="/privacypolicy"
                  style={{
                    color: 'var(--primary)',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    textDecoration: 'underline',
                    marginRight: 12,
                  }}
                >
                  Privacy Policy
                </a>
              </div>
            </div>
          </div>
        </div>

        {isScrollable && (
          isAtBottom ? (
            <ArrowCue direction="up"  onClick={scrollOneViewportUp}   fixed />
          ) : (
            <ArrowCue direction="down" onClick={scrollOneViewportDown} fixed />
          )
        )}

        {/* ======================= Bots Showcase (NEW) ======================= */}
        <section
  style={{ borderTop: '1px solid #eef2f7' }}
>
  <div className="split-screen">
    {/* LEFT: Example cards (aligned to center line, same inner gutter) */}
    <div className="left-side" style={{ width: '50vw' }}>
      <div
        // style={{
        //   width: '100%',
        //   padding: '56px 0',                 // vertical rhythm same as hero
        //   display: 'flex',
        //   justifyContent: 'flex-end',        // push content toward the center
        // }}
        className="margin-top-30vh" style={{height: '100vh', marginLeft: '15vh' }}
      >
        <div style={{ width: '80%', padding: '0 40px', }}>
          <div style={{ display: 'grid', gap: 16 }}>
            <BotCard
              title="Resume Bot"
              badge="Popular"
              desc="Embed a bot trained on your resume to answer recruiter questions in your tone. Share as a link or iframe."
              onClick={() => navigate('/login')}
            />
            <BotCard
              title="Site Assistant"
              desc="Turn your docs/FAQ into a helpful on-site assistant. Add a branded launcher and track engagement."
              onClick={() => navigate('/login')}
            />
            <BotCard
              title="Canvas Bot"
              desc="Create a focused bot around a any uploaded information or brief. Great for portfolios, pitch decks, and demos."
              onClick={() => navigate('/login')}
            />
          </div>
        </div>
      </div>
    </div>

    {/* RIGHT: Copy + CTA (exact same inner width/padding as hero right) */}
    <div className="right-side" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <div style={{ width: '100%', maxWidth: 520, padding: '56px 40px' }}>
        <Text
          text="Create, Embed, and Share Bots — in Minutes"
          fontSize="28px"
          fontWeight="700"
          lineHeight="34px"
          color="#030303"
          textAlign="left"
        />
        <div style={{ height: 12 }} />
        <Text
          text="Pick a template, upload your files (PDFs, images, docs), and instantly get an embeddable assistant. Customize the theme to match your brand and share with a single link."
          fontSize="16px"
          fontWeight="400"
          lineHeight="28px"
          color="#111827"
          textAlign="left"
        />
        <div style={{ height: 8 }} />
        <ul style={{ margin: '8px 0 0 18px', color: '#111827', fontFamily: 'Montserrat', fontSize: 14, lineHeight: '24px' }}>
          <li>One-click iframe embed with launcher</li>
          <li>Purple/Blue/Green themes + custom colors</li>
          <li>Session persistence, analytics, and share links</li>
        </ul>
        <div style={{ marginTop: 20, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Button label="Build a Bot" height="42px" onClick={() => navigate('/login')} />
          <Button
            label="View Examples"
            height="42px"
            onClick={() => navigate('/login')}
            style={{
              background: '#fff',
              color: 'var(--primary)',
              border: '1px solid var(--primary)',
            }}
          />
        </div>
      </div>
    </div>
  </div>
</section>
<section
  style={{ borderTop: '1px solid #eef2f7'}}
>
  <div className="split-screen">
    {/* LEFT: Example cards (aligned to center line, same inner gutter) */}
    <div className="left-side-border-top-right-16 color-purple-background" style={{ width: '50vw' }}>
      <div
        // style={{
        //   width: '100%',
        //   padding: '56px 0',                 // vertical rhythm same as hero
        //   display: 'flex',
        //   justifyContent: 'flex-end',        // push content toward the center
        // }}
        className="margin-top-30vh" style={{height: '100vh', marginLeft: '15vh' }}
      >
        <div style={{ width: '80%', padding: '0 40px', }}>
          <ChatEmbed inline></ChatEmbed>
        </div>
      </div>
    </div>

    {/* RIGHT: Copy + CTA (exact same inner width/padding as hero right) */}
    <div className="right-side" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <div style={{ width: '100%', maxWidth: 520, padding: '56px 40px' }}>
        <Text
          text="Customize Every Detail of Your Embeded Assistant"
          fontSize="28px"
          fontWeight="700"
          lineHeight="34px"
          color="#030303"
          textAlign="left"
        />
        <div style={{ height: 12 }} />
        <Text
          text="Brand the chat with simple URL params: set theme color, fonts, corner radius, launcher behavior, size, welcome text, and preset suggestions. The preview updates instantly."
          fontSize="16px"
          fontWeight="400"
          lineHeight="28px"
          color="#111827"
          textAlign="left"
        />
        <div style={{ height: 8 }} />
        <ul style={{ margin: '8px 0 0 18px', color: '#111827', fontFamily: 'Montserrat', fontSize: 14, lineHeight: '24px' }}>
          <li>
            Theme, font & radius: <code>theme</code>, <code>font</code>, <code>fontUrl</code>, <code>radius</code>
          </li>
          <li>
            Placement & size: <code>mode</code>/<code>launcher</code>, <code>w</code>, <code>h</code>
          </li>
          <li>
            Onboarding & session: <code>welcome</code>, multiple <code>suggest</code>, <code>allowSession</code>
          </li>
        </ul>
        <div style={{ marginTop: 20, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Button label="Customize Embed" height="42px" onClick={() => navigate('/login')} />
          <Button
            label="View Embed Docs"
            height="42px"
            onClick={() => navigate('/login')}
            style={{
              background: '#fff',
              color: 'var(--primary)',
              border: '1px solid var(--primary)',
            }}
          />
        </div>
      </div>
    </div>
  </div>
</section>

        {/* ===================== End Bots Showcase (NEW) ===================== */}
      </div>
    </motion.div>
  );
};

export default Home;
